import { useLang } from 'hooks/lang';
import { FormEvent } from 'react';

import { actions } from 'entities';

import {
  IconButton,
  Button,
  Column,
  Row,
  Avatar,
  LabeledInput,
  LabeledSelect,
  Card,
} from 'components/Blocks';
import { EntitySelect } from 'components/Entities';

import {
  ArrowLeftIcon,
  BuildingIcon,
  LanguagesIcon,
  MailIcon,
  UserBookIcon,
  WrenchIcon,
} from 'components/Icons';

import ChangeTeam from './ChangeTeam';
import UserSampleId from './SampleId';
import { UserActionTabWrap } from './Actions/UserActionWrap';

import { useTeamProfile } from '../../context';
import { useFormControl } from 'hooks';

import { UserScreenProps } from './types';

function UserScreen({ goBack, user, refreshUsers, deleteUser }: UserScreenProps) {
  const form = useFormControl(user, { watch: true });

  const { t } = useLang();
  const { team } = useTeamProfile();

  const isUpdate = !!user.id;

  const createUser = actions.users.createUser.use({
    onSuccess: (mutatedUser) => {
      refreshUsers({ mutatedUser, isUpdate });

      goBack();
    },
  });

  const updateUser = actions.users.updateUser.use({
    onSuccess: (mutatedUser) => {
      refreshUsers({ mutatedUser, isUpdate });
    },
  });

  const loading = updateUser.loading || createUser.loading;

  const handleSaveClick = (e: FormEvent) => {
    e.preventDefault();

    const { id, name, email, office, type, language } = form.state;

    const payload = {
      id,
      name,
      email,
      office,
      type,
      language,
      team: team.id,
    };

    isUpdate ? updateUser.execute(payload) : createUser.execute(payload);
  };

  return (
    <Card flow="column">
      <Row>
        <IconButton onClick={goBack}>
          <ArrowLeftIcon />
        </IconButton>

        {isUpdate && <Avatar src={user.picture || ''} fallback="no-user-avatar" />}

        <h6>{`${isUpdate ? t('edit') : t('create')} ${t('user')}`}</h6>
      </Row>

      <UserActionTabWrap>
        <Column as="form" gap="6" className="mx-auto w-full max-w-4xl" onSubmit={handleSaveClick}>
          <LabeledInput
            value={form.state?.name || ''}
            onChange={form.handleChange}
            label={t('name')}
            name="name"
            required
            labelIcon={UserBookIcon}
          />

          <Column gap="2">
            <LabeledInput
              value={form.state?.email || ''}
              onChange={form.handleChange}
              label={t('email')}
              name="email"
              required
              disabled={isUpdate}
              labelIcon={MailIcon}
            />

            {!isUpdate && (
              <p className="text-end text-sm italic text-muted-foreground">
                {t('user-is-slack-email')}
              </p>
            )}
          </Column>

          {isUpdate && <UserSampleId user={form.state} refreshUsers={refreshUsers} />}

          <EntitySelect
            target="offices.list"
            id="office"
            label={t('office')!}
            value={form.state?.office || ''}
            onChange={form.handleChange}
            labelKey="name"
            labelIcon={BuildingIcon}
          />

          <Row className="[&>div]:w-1/2">
            <LabeledSelect
              id="type"
              label={t('type') as string}
              value={form.state?.type}
              onChange={form.handleChange}
              labelIcon={WrenchIcon}
              options={[
                { value: 'LEADER', label: t('leader') },
                { value: 'USER', label: t('normal-user') },
              ]}
            />

            <LabeledSelect
              id="language"
              label={t('language')}
              value={form.state?.language}
              onChange={form.handleChange}
              labelIcon={LanguagesIcon}
              options={[
                { value: 'en', label: t('english') },
                { value: 'es', label: t('portuguese') },
                { value: 'pt', label: t('spanish') },
              ]}
            />
          </Row>

          <Button type="submit" loading={loading}>
            {t('save')}
          </Button>

          {isUpdate && (
            <ChangeTeam
              user={user}
              onFinish={() => {
                deleteUser();
                goBack();
              }}
            />
          )}
        </Column>
      </UserActionTabWrap>
    </Card>
  );
}

export default UserScreen;
